<template>
  <!-- Profile Dropdown Menu -->
  <transition name="dropdown">
    <div
      v-if="show"
      class="absolute top-12 right-0 z-50 w-64 rounded-lg bg-surface border border-line shadow-2xl overflow-hidden"
      @click.stop
    >
      <div class="p-4 border-b border-line">
        <p class="text-content font-semibold text-sm">{{ userId }}</p>
        <p class="text-muted text-xs mt-1">{{ lastLoginTime }}</p>
      </div>
      <div class="py-2">
        <button
          @click="handleMenuClick('LOGIN_QRCODE')"
          class="w-full px-4 py-2 text-left text-content text-sm hover:bg-content/5 transition-colors"
        >
          {{ $t('profile.loginWithQRCode') }}
        </button>

        <!-- <div class="my-2 border-t border-content/10"></div> -->

        <!-- 사용자 등록 정보 (클릭 시 편집 모달) -->
        <button
          @click="handleMenuClick('USER_INFO')"
          class="w-full px-4 py-2 text-left text-content text-sm hover:bg-content/5 transition-colors"
        >
          {{ $t('profile.userInfo') }}
        </button>

        <!-- Language Toggle -->
        <button
          @click="handleToggleLanguage"
          class="w-full px-4 py-2 text-left text-content text-sm hover:bg-content/5 transition-colors flex items-center justify-between"
        >
          <span>{{ $t('profile.language') }}</span>
          <span class="text-xs text-muted">{{ currentLocale === 'ko' ? '한국어' : 'English' }}</span>
        </button>

        <!-- Theme Toggle (Mode) — 언어 아래 위치. 기본 다크 -->
        <button
          @click="handleToggleTheme"
          class="w-full px-4 py-2 text-left text-content text-sm hover:bg-content/5 transition-colors flex items-center justify-between"
        >
          <span>{{ $t('profile.theme') }}</span>
          <span class="text-xs text-muted">{{ isDarkMode ? $t('profile.darkMode') : $t('profile.lightMode') }}</span>
        </button>

         <!-- 번역 관리 (라우트 미연결 - 추후 구현) -->
        <button
          @click="handleMenuClick('TRANSLATION')"
          class="w-full px-4 py-2 text-left text-content text-sm hover:bg-content/5 transition-colors"
        >
          {{ $t('profile.translationManagement') }}
        </button>

        <!-- 라이선스 (우측에 타입 표시). 클릭 이동 주석처리 — 표시 전용.
             재활성: 아래 button 에 @click="handleMenuClick('/admin/billing')" 복구 -->
        <!-- KOGAS 빌드에서 숨김($feat) -->
        <div
          v-if="$feat('profileLicense')"
          class="w-full px-4 py-2 text-left text-content text-sm flex items-center justify-between cursor-default"
        >
          <span>{{ $t('profile.licenseView') }}</span>
          <span class="text-xs text-muted">{{ licenseLabel }}</span>
        </div>

        <!-- Admin Menu -->
        <!-- <template v-if="isAdmin">
          <div class="my-2 border-t border-line"></div>
          <div class="px-4 py-2">
            <p class="text-yellow-400 text-xs font-bold">{{ $t('profile.adminMenu') }}</p>
          </div>
          <button
            @click="handleMenuClick('/admin/company')"
            class="w-full px-4 py-2 text-left text-yellow-400 text-sm hover:bg-content/5 transition-colors"
          >
            └ {{ $t('profile.companyManagement') }}
          </button>
          <button
            @click="handleMenuClick('/screen')"
            class="w-full px-4 py-2 text-left text-yellow-400 text-sm hover:bg-content/5 transition-colors"
          >
            └ TEST - Screen Share
          </button>
          
        </template> -->
      </div>
    </div>
  </transition>

  <!-- Overlay to close menu -->
  <div
    v-if="show"
    @click="$emit('close')"
    class="fixed inset-0 z-40"
  ></div>

  <!-- Login QR Code Modal -->
  <ModalLoginQRCode
    v-if="showModalLoginQRCode"
    @close="closeModalLoginQRCode"
  />

  <!-- 번역 관리 = 언어(번역) 설정 팝업 (AR Remote 환경설정 모달 재사용) -->
  <ModalRemoteEnvirSetting
    v-if="showModalEnvirSetting"
    @close="showModalEnvirSetting = false"
  />
</template>

<script>
import { defineAsyncComponent } from 'vue'
import axios from 'axios'
import { AUTH_URL } from '@/config'
import ModalLoginQRCode from '@/views/layout/ModalLoginQRCode.vue'

export default {
  name: 'ProfileDropdownMenu',
  components: {
    ModalLoginQRCode,
    // 번역 관리 = AR Remote 의 언어(번역) 설정 팝업 (크로스모듈 → lazy import)
    ModalRemoteEnvirSetting: defineAsyncComponent(() =>
      import('@modules/remote/components/ModalRemoteEnvirSetting.vue')
    )
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    userId: {
      type: String,
      required: true
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    isDarkMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showModalLoginQRCode: false,
      showModalEnvirSetting: false,
      lastLoginTime: '',
      licenseType: '', // 현재 사용자 라이선스 타입 코드 (TRIAL/BASIC/...)
      licenseName: '', // 표시명(국문, master_license.name)
      licenseNameEng: '' // 표시명(영문, master_license.name_eng)
    }
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale
    },
    // 라이선스 표시명 — 언어에 따라 name/name_eng 선택. 없으면 코드, 그것도 없으면 '-'
    licenseLabel() {
      const isEn = String(this.currentLocale || '').toLowerCase().startsWith('en')
      const name = isEn ? (this.licenseNameEng || this.licenseName) : (this.licenseName || this.licenseNameEng)
      return name || this.licenseType || '-'
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        const lastAccessTime = this.$store.state.loginStore.userInfo?.LastAccessTime;
        this.lastLoginTime = this.$t('profile.lastAccessTime', { time: lastAccessTime || '-' })
        // 라이선스 표시가 켜진 테넌트만 조회(KOGAS 등 숨김 빌드는 불필요한 API 스킵)
        if (this.$feat('profileLicense') && !this.licenseType) this.fetchLicense() // 최초 오픈 시 1회 조회
      }
    }
  },
  methods: {
    // 현재 사용자 라이선스 조회 (표시용 — 실패해도 무시)
    async fetchLicense() {
      try {
        const { data } = await axios.get(`${AUTH_URL}/license`)
        this.licenseType = data?.license_type || ''
        this.licenseName = data?.name || ''
        this.licenseNameEng = data?.name_eng || ''
      } catch (e) {
        // 표시용이라 조용히 무시
      }
    },
    handleMenuClick(menuUrl) {
      this.$emit('close');

      if (menuUrl == "LOGIN_QRCODE") {
        this.showModalLoginQRCode = true;
      }
      else if (menuUrl == "USER_INFO") {
        // 사용자 등록 정보 = 내 계정 페이지로 이동 (프로필/비밀번호/PIN/회원탈퇴)
        this.$router.push('/account').catch(() => {});
      }
      else if (menuUrl == "TRANSLATION") {
        // 번역 관리 = AR Remote 언어(번역) 설정 팝업 열기
        this.showModalEnvirSetting = true;
      }
      else if (menuUrl.startsWith("/admin/")) {
        this.$router.push(menuUrl).catch(() => {});
      }
      else if (menuUrl.startsWith("/screen")) {
        this.$router.push(menuUrl).catch(() => {});
      }
    },

    handleToggleTheme() {
      this.$emit('toggle-theme');
    },

    handleToggleLanguage() {
      this.$i18n.locale = this.$i18n.locale === 'ko' ? 'en' : 'ko'
    },

    closeModalLoginQRCode() {
      this.showModalLoginQRCode = false;
    }
  }
}
</script>

<style scoped>
  /* Dropdown Animation */
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .dropdown-enter-from,
  .dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  .dropdown-enter-to,
  .dropdown-leave-from {
    opacity: 1;
    transform: translateY(0);
  }
</style>
